import api from './api';

export interface Discussion {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: string;
  messageCount: number;
  status: 'active' | 'completed' | 'pending' | 'closed';
  createdAt: string;
  rating?: number;
  feedbackComment?: string;
  closedAt?: string;
}

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: string;
}

export interface DiscussionDetail {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

class DiscussionApiService {
  async getUserDiscussions(): Promise<{ discussions: Discussion[]; count: number }> {
    try {
      const response = await api.get('/api/discussions/');
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des discussions');
    }
  }

  async getDiscussionMessages(discussionId: string): Promise<{ discussion: DiscussionDetail; messages: Message[] }> {
    try {
      const response = await api.get(`/api/discussions/${discussionId}/messages/`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la récupération des messages');
    }
  }

  async createDiscussion(title: string, message: string): Promise<{ discussion: any; message: string }> {
    try {
      const response = await api.post('/api/discussions/create/', {
        title,
        message
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la création de la discussion');
    }
  }

  async addMessage(discussionId: string, content: string): Promise<{ message: Message }> {
    try {
      const response = await api.post(`/api/discussions/${discussionId}/messages/add/`, {
        content,
        role: 'user'
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de l\'ajout du message');
    }
  }

  async closeDiscussion(discussionId: string, rating?: number, feedbackComment?: string): Promise<{ message: string; discussion: Discussion }> {
    try {
      const response = await api.post(`/api/discussions/${discussionId}/close/`, {
        rating,
        feedback_comment: feedbackComment
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la fermeture de la discussion');
    }
  }

  async reopenDiscussion(discussionId: string): Promise<{ message: string; discussion: Discussion }> {
    try {
      const response = await api.post(`/api/discussions/${discussionId}/reopen/`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Erreur lors de la réouverture de la discussion');
    }
  }

  async rateDiscussion(rating: number, comment?: string): Promise<void> {
    try {
      const response = await api.post('/api/discussions/rate/', {
        rating,
        comment: comment || ''
      });
      
      if (response.status !== 200) {
        throw new Error('Erreur lors de la notation');
      }
    } catch (error: any) {
      console.error('Erreur lors de la notation:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la notation');
    }
  }

  // Vérifier le statut de la discussion active
  async checkDiscussionStatus(): Promise<{ isClosed: boolean; discussionId?: string }> {
    try {
      const response = await api.get('/api/discussions/check-active/');
      
      if (response.data.hasActiveDiscussion) {
        // Il y a une discussion active, vérifier son statut
        const discussionsResponse = await this.getUserDiscussions();
        const activeDiscussion = discussionsResponse.discussions.find((d: Discussion) => d.status === 'active');
        
        if (!activeDiscussion) {
          // Plus de discussion active = fermée
          return { isClosed: true };
        }
        
        return { isClosed: false, discussionId: activeDiscussion.id };
      } else {
        // Pas de discussion active, vérifier s'il y en a une fermée récemment
        const discussionsResponse = await this.getUserDiscussions();
        const recentClosed = discussionsResponse.discussions.find((d: Discussion) => d.status === 'closed');
        
        return { 
          isClosed: !!recentClosed, 
          discussionId: recentClosed?.id 
        };
      }
    } catch (error) {
      console.error('Erreur vérification statut discussion:', error);
      return { isClosed: false };
    }
  }
}

export const discussionApiService = new DiscussionApiService();
